import * as React from 'react'
import { X } from '@phosphor-icons/react'
import { RatingScaleLabel } from './types'

// Figma: 544px card, 480px content column, p-8, gap-8 between content and the
// absolutely-positioned close button. slate/stone outlines map onto gray.

export function CurrentRatingScalesModal({
  labels,
  onClose,
}: {
  labels: RatingScaleLabel[]
  onClose: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Current rating scales"
        className="relative flex max-h-[85vh] w-[544px] flex-col rounded-2xl bg-white shadow-[0px_16px_64px_-8px_rgba(15,20,30,0.32),0px_6px_8px_-2px_rgba(15,20,30,0.08)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-8 right-8 z-10 h-8 w-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500"
        >
          <X weight="bold" className="h-4 w-4" />
        </button>
        <div className="min-h-0 p-8 flex flex-col items-end gap-8 overflow-y-auto">
          <div className="w-[480px] flex flex-col gap-6">
            <h2 className="m-0 font-talent text-2xl font-medium text-indigo-950 leading-7">
              Current rating scales
            </h2>

            {labels.length === 0 ? (
              <p className="m-0 font-talent text-base font-normal text-gray-500 leading-5">
                No rating scales configured.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {labels.map((label, i) => (
                  <div
                    key={label.labelSlug}
                    className="p-3 bg-white rounded-lg border border-gray-200 flex flex-col gap-2"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1 flex items-center gap-4">
                        <span className="font-talent text-base font-normal text-gray-500 leading-5">
                          Scale {i + 1}
                        </span>
                      </div>
                      <div className="h-6 px-1 py-0.5 bg-white rounded-sm border border-gray-200 flex items-center gap-1">
                        <span className="pl-0.5 flex">
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: label.color }}
                          />
                        </span>
                        <span className="pr-0.5 font-sans text-xs font-normal text-indigo-950 leading-5">
                          {label.labelName}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="m-0 font-talent text-base font-medium text-indigo-950 leading-5">
                        {label.labelName}
                      </p>
                      {label.description && (
                        // Descriptions are rich text sanitised server-side
                        // (CustomRatingScale#sanitize_label_descriptions), so
                        // render the markup rather than escaping it — matching
                        // the Custom Rating Scales listing card.
                        <div
                          className="font-talent text-base font-normal text-gray-500 leading-5 [&_p]:m-0 [&_p]:mb-1 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:underline [&_a]:text-green-700"
                          dangerouslySetInnerHTML={{
                            __html: label.description,
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
